import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Headphones, Lock, ChevronRight, X, Timer, Infinity as InfinityIcon, RefreshCw } from 'lucide-react';
import { trackAndRedirect } from '../../lib/tracking';
function PopupParticles() {
  const particles = Array.from({
    length: 15
  });
  return <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]" data-id="element-390">
      {particles.map((_, i) => {
      const left = `${Math.random() * 100}%`;
      const delay = `${Math.random() * 5}s`;
      const duration = `${8 + Math.random() * 7}s`;
      const size = Math.random() > 0.5 ? 'w-1 h-1' : 'w-1.5 h-1.5';
      return <div key={i} className={`absolute bottom-0 rounded-full bg-gold/50 ${size}`} style={{
        left,
        animation: `particleFloat ${duration} linear ${delay} infinite`
      }} data-id="element-391" />;
    })}
    </div>;
}
interface OfertaEspecialPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
export function OfertaEspecialPopup({
  isOpen,
  onClose
}: OfertaEspecialPopupProps) {
  const [timeLeft, setTimeLeft] = useState(14 * 60 + 55);
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  return <AnimatePresence data-id="element-392">
      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-6" data-id="element-393">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/90 backdrop-blur-md" data-id="element-394" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-xl max-h-[95vh] overflow-y-auto hide-scrollbar bg-[#0c0c0e] rounded-2xl sm:rounded-[2rem] shadow-[0_0_60px_rgba(212,175,55,0.2)] border border-gold/40" data-id="element-395">
            {/* Golden Energy Background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.15),transparent_70%)] pointer-events-none" data-id="element-396" />
            <PopupParticles data-id="element-397" />

            <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 z-50 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors" data-id="element-398">
              <X className="w-4 h-4" data-id="element-399" />
            </button>

            <div className="relative z-10 p-4 sm:p-10 text-center" data-id="element-400">
              {/* Timer */}
              <div className="inline-flex items-center gap-1.5 text-white/50 text-[10px] sm:text-xs font-medium mb-3 sm:mb-6" data-id="element-401">
                <Timer className="w-3.5 h-3.5" data-id="element-402" />
                Esta ventana se cierra en: {formatTime(timeLeft)}
              </div>

              {/* Headline */}
              <h2 className="font-serif text-xl sm:text-4xl font-black text-white mb-2 sm:mb-4 leading-tight drop-shadow-md" data-id="element-403">
                Espera… tu acceso <br className="hidden sm:block" data-id="element-404" />
                <span className="text-gold" data-id="element-405">aún no está completo</span>
              </h2>

              {/* Subheadline */}
              <p className="text-xs sm:text-base text-white/80 font-medium mb-4 sm:mb-8 leading-relaxed max-w-md mx-auto" data-id="element-406">
                Acabas de activar el sistema base.
                <br data-id="element-407" />
                Pero sin esto…
                <br data-id="element-408" />
                <span className="text-white font-bold" data-id="element-409">
                  te quedas fuera de la parte más importante.
                </span>
              </p>

              {/* Main Block */}
              <div className="bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-2xl p-4 sm:p-8 mb-4 sm:mb-8 relative overflow-hidden group" data-id="element-410">
                <div className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/5 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer pointer-events-none" data-id="element-411" />

                <h3 className="text-gold font-black text-base sm:text-xl mb-2 sm:mb-3 relative z-10" data-id="element-412">
                  Acceso a tu espacio privado VIP de transformación
                </h3>
                <p className="text-[11px] sm:text-sm text-white/70 mb-4 sm:mb-6 max-w-sm mx-auto relative z-10" data-id="element-413">
                  Aquí es donde ocurre el proceso real:
                  <br data-id="element-414" />
                  escuchas los audios, aplicas el sistema
                  <br data-id="element-415" />y creas tu rutina diaria.
                </p>

                {/* VIP Room */}
                <div className="bg-[#0A0A0B] border border-gold/30 rounded-xl p-3 sm:p-6 mb-4 sm:mb-6 shadow-[0_10px_30px_rgba(212,175,55,0.15)] relative z-10" data-id="element-416">
                  <h4 className="text-white font-bold text-xs sm:text-base mb-3 sm:mb-4 flex items-center justify-center gap-2" data-id="element-417">
                    <Lock className="w-4 h-4 text-gold" data-id="element-418" /> Tu espacio privado
                  </h4>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 text-left max-w-xs mx-auto" data-id="element-419">
                    <div className="flex items-center gap-2" data-id="element-420">
                      <Headphones className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold shrink-0" data-id="element-421" />
                      <span className="text-white/90 text-[11px] sm:text-sm font-medium" data-id="element-422">
                        Audios guiados
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-423">
                      <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold shrink-0" data-id="element-424" />
                      <span className="text-white/90 text-[11px] sm:text-sm font-medium" data-id="element-425">
                        Frecuencia 888Hz
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-426">
                      <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold shrink-0" data-id="element-427" />
                      <span className="text-white/90 text-[11px] sm:text-sm font-medium" data-id="element-428">
                        Acceso privado
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-429">
                      <InfinityIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold shrink-0" data-id="element-430" />
                      <span className="text-white/90 text-[11px] sm:text-sm font-medium" data-id="element-431">
                        Acceso de por vida
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-432">
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold shrink-0" data-id="element-433" />
                      <span className="text-white/90 text-[11px] sm:text-sm font-medium" data-id="element-434">
                        Futuras actualizaciones
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bonuses */}
                <div className="relative z-10" data-id="element-435">
                  <h4 className="text-white/50 font-bold text-[10px] sm:text-xs uppercase tracking-wider mb-2 sm:mb-3" data-id="element-436">
                    Protocolos adicionales incluidos
                  </h4>
                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2" data-id="element-437">
                    {['Activación interna', 'Flujo mental', 'Manifestación guiada', 'Estructura de riqueza'].map((bono, i) => <span key={i} className="bg-white/5 border border-white/10 text-white/80 text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md" data-id="element-438">
                        {bono}
                      </span>)}
                  </div>
                </div>
              </div>

              {/* Price & CTA */}
              <div className="mb-4 sm:mb-6" data-id="element-439">
                <p className="text-lg sm:text-2xl font-black text-white mb-1" data-id="element-440">
                  Acceso completo hoy: <span className="text-gold" data-id="element-441">$26.39</span>
                </p>
                <p className="text-white/50 text-[10px] sm:text-xs font-medium" data-id="element-442">
                  Pago único · Acceso inmediato
                </p>
              </div>

              <p className="text-gold/90 text-[11px] sm:text-sm font-bold mb-3 sm:mb-4" data-id="element-443">
                Esto es lo que convierte el sistema en un proceso real.
              </p>

              <button onClick={e => {
            e.preventDefault();
            trackAndRedirect(26.39, 'Complete_Experience', 'https://betterdailyguides.com/cart/51978554671406:1');
          }} className="relative inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold via-yellow-400 to-gold text-dark font-black text-xs sm:text-base px-6 sm:px-12 py-3.5 sm:py-5 rounded-xl shadow-[0_15px_40px_rgba(212,175,55,0.4)] uppercase tracking-wider hover:shadow-[0_20px_50px_rgba(212,175,55,0.6)] hover:bg-yellow-400 active:scale-[0.97] transition-all duration-300 w-full overflow-hidden group" data-id="element-444">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:animate-shimmer" data-id="element-445" />
                <span className="relative z-10 flex items-center gap-2" data-id="element-446">
                  QUIERO MI ACCESO COMPLETO
                  <ChevronRight className="w-5 h-5" data-id="element-447" />
                </span>
              </button>

              {/* Reject Button */}
              <div className="mt-5 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10" data-id="element-448">
                <button onClick={e => {
              e.preventDefault();
              trackAndRedirect(17.0, 'Main_Offer', 'https://betterdailyguides.com/cart/51973733482798:1');
            }} className="text-white/60 hover:text-white/90 text-xs sm:text-sm font-medium transition-colors inline-block mb-2 sm:mb-3 underline underline-offset-2 decoration-white/30" data-id="element-449">
                  Continuar solo con la versión básica
                </button>
                <div className="text-white/30 text-[10px] sm:text-xs text-left max-w-[200px] mx-auto" data-id="element-450">
                  <p className="mb-1" data-id="element-451">Sin este acceso, no tendrás:</p>
                  <ul className="space-y-0.5" data-id="element-452">
                    <li data-id="element-453">– Los audios guiados</li>
                    <li data-id="element-454">– La experiencia completa</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}